import {useCallback, useState} from "react";
import {Button} from "@aws-amplify/ui-react";
import {BookmarkAdd, BookmarkRemove} from "@mui/icons-material";
import {CircularProgress} from "@mui/material";
import {HoverPopoverUI} from "./HoverPopoverUI";

/**
 * @typedef {Object} TrackProductButtonProps
 * @property {boolean} isTracked - Whether the product is already tracked.
 * @property {() => Promise<void>} onTrack - Function to track the product.
 * @property {() => Promise<void>} onUntrack - Function to untrack the product.
 */

/**
 * Handle tracking/untracking logic separately.
 *
 * @param {boolean} isTracked
 * @param {() => Promise<void>} onTrack
 * @param {() => Promise<void>} onUntrack
 */
const trackOrUntrackProduct = async (isTracked, onTrack, onUntrack) => {
    if (isTracked) {
        await onUntrack();
    } else {
        await onTrack();
    }
};

/**
 * TrackProductButton Component: Button to track/untrack a product, with loading state.
 *
 * @param {TrackProductButtonProps} props
 * @returns {JSX.Element}
 */
export const TrackProductButton = ({isTracked, onTrack, onUntrack}) => {
    const [loading, setLoading] = useState(false);

    const handleClick = useCallback(async () => {
        setLoading(true);
        try {
            await trackOrUntrackProduct(isTracked, onTrack, onUntrack);
        } catch (error) {
            console.error('Failed to track/untrack product:', error);
        } finally {
            setLoading(false);
        }
    }, [isTracked, onTrack, onUntrack]);

    const getButtonIcon = () => {
        if (loading) {
            return <CircularProgress size={20} />;
        }
        return isTracked ? <BookmarkRemove/> : <BookmarkAdd/>;
    };

    const getPopoverMessage = () => (isTracked ? 'Quitar de productos pendientes' : 'Agregar a productos pendientes');

    return (
        <HoverPopoverUI message={getPopoverMessage()}>
            <Button
                variant="contained"
                className="btn btn-warning"
                onClick={handleClick}
                disabled={loading}
            >
                {getButtonIcon()}
            </Button>
        </HoverPopoverUI>
    );
};
